import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Container, Paper, Typography, Button, Grid, CircularProgress } from '@mui/material';
import {
	confirmRegistrationApi,
	rejectRegistrationApi,
	confirmRegistrationAsNafilApi,
	getConfirmationPageInfoApi,
} from '../../../api/visitorApi';

const ItikaafEventConfirmation = () => {
	const [loading, setLoading] = useState(false);
	const [infoLoading, setInfoLoading] = useState(true);
	const [pageInfo, setPageInfo] = useState(null);
	const [infoError, setInfoError] = useState('');

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { registrationId } = useParams();

	useEffect(() => {
		dispatch(
			getConfirmationPageInfoApi(
				registrationId,
				setInfoLoading,
				(data) => setPageInfo(data),
				(err) => setInfoError(err?.message || 'This confirmation link is invalid or has expired.')
			)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [registrationId]);

	const handleYes = () => {
		setLoading(true);
		dispatch(
			confirmRegistrationApi(
				registrationId,
				setLoading,
				() => {
					navigate('/');
				},
				() => {
					setLoading(false);
				}
			)
		);
	};

	const handleNo = () => {
		setLoading(true);
		dispatch(
			rejectRegistrationApi(
				registrationId,
				setLoading,
				() => {
					navigate('/');
				},
				() => {
					setLoading(false);
				}
			)
		);
	};

	const handleNafil = () => {
		setLoading(true);
		dispatch(
			confirmRegistrationAsNafilApi(
				registrationId,
				setLoading,
				() => {
					navigate('/');
				},
				() => {
					setLoading(false);
				}
			)
		);
	};

	let statusMessage = '';
	if (pageInfo) {
		if (pageInfo.status !== 'ACCEPTED') {
			statusMessage = 'This registration is not in a state that can be confirmed here.';
		} else if (pageInfo.isUserConfirmedAction) {
			statusMessage = pageInfo.isUserConfirmed
				? `You have already confirmed your participation (${pageInfo.itakaafType === 'nafil' ? 'Nafil' : 'Sunnah'}). Please contact administration to make changes.`
				: 'You have already declined your participation. Please contact administration to make changes.';
		}
	}

	const canRespond = pageInfo && pageInfo.status === 'ACCEPTED' && !pageInfo.isUserConfirmedAction;

	return (
		<Container maxWidth='md' sx={{ mt: 6 }}>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					borderRadius: 2,
					backgroundColor: '#ffffff',
					boxShadow: 3,
				}}
			>
				<Typography
					variant='h4'
					align='center'
					gutterBottom
					sx={{ color: '#0e5472', fontWeight: 'bold', marginBottom: '30px' }}
				>
					I'tikaf 2026 - Participation Confirmation
				</Typography>

				{infoLoading && (
					<Grid container justifyContent='center' sx={{ my: 4 }}>
						<CircularProgress />
					</Grid>
				)}

				{!infoLoading && infoError && (
					<Typography variant='body1' align='center' sx={{ color: '#c62828' }}>
						{infoError}
					</Typography>
				)}

				{!infoLoading && !infoError && statusMessage && (
					<Typography variant='body1' align='center' sx={{ color: '#555', mt: 2 }}>
						{statusMessage}
					</Typography>
				)}

				{!infoLoading && !infoError && canRespond && (
					<>
						<Typography
							variant='body1'
							gutterBottom
							sx={{ textAlign: 'justify', mt: 2 }}
						>
							Need your prompt confirmation about the I'tikaf program in the
							auspicious company of Hazrath Maulana Sayyed Muhammad Talha Qasmi
							Naqshbandi Mujaddidi DB at Masjid Jame-Al-Ehsan, Khanqah Darul Ehsan,
							Bengaluru.
						</Typography>
						<Typography variant='body1' gutterBottom sx={{ textAlign: 'justify' }}>
							As we have received many requests for I'tikaf and we have limited space
							inside the mosque, confirming your participation at the earliest will
							help accommodate others on the waiting list.
						</Typography>
						<Typography variant='body1' gutterBottom sx={{ textAlign: 'justify' }}>
							Please choose from below options:
						</Typography>
						<Grid container spacing={2} justifyContent='center' sx={{ mt: 2 }}>
							<Grid item xs={12} sm={pageInfo.allowConfirmAsNafil ? 4 : 6}>
								<Button
									variant='contained'
									color='primary'
									fullWidth
									onClick={handleYes}
									disabled={loading}
								>
									Yes – Participate
								</Button>
							</Grid>
							{pageInfo.allowConfirmAsNafil && (
								<Grid item xs={12} sm={4}>
									<Button
										variant='contained'
										color='warning'
										fullWidth
										onClick={handleNafil}
										disabled={loading}
									>
										Yes – But for Nafil
									</Button>
								</Grid>
							)}
							<Grid item xs={12} sm={pageInfo.allowConfirmAsNafil ? 4 : 6}>
								<Button
									variant='contained'
									color='error'
									fullWidth
									onClick={handleNo}
									disabled={loading}
								>
									No – Not Participating
								</Button>
							</Grid>
						</Grid>
					</>
				)}
			</Paper>
		</Container>
	);
};

export default ItikaafEventConfirmation;
